import { RefObject, useState } from "react";
import ClauseSelectionPopover from "./ClauseSelectionPopover";
import SelectionActionButton from "./SelectionActionButton";
import { ITextSelectionConfig, SectionResolver, SelectedTextResult, TextSelectionDomConfig } from "./textSelection.types";
import { useTextSelectionCapture } from "./useTextSelectionCapture";

export interface ITextSelectionLayerProps {
    config: ITextSelectionConfig;
    /** The viewer element the selection has to happen inside. */
    rootRef: RefObject<HTMLElement | null>;
    domConfig: TextSelectionDomConfig;
    /** Changes whenever the rendered text is rebuilt (a new document, a new zoom level). */
    resetKey?: string;
    /** Supplied by a viewer whose rendering carries no heading structure to read the section from. */
    resolveSection?: SectionResolver;
}

export default function TextSelectionLayer(props: ITextSelectionLayerProps) {
    const [editing, setEditing] = useState<{ captured: SelectedTextResult; position: { top: number; left: number } } | undefined>(undefined);

    // While the popover is open the document selection belongs to its text fields, so the watcher must
    // stop treating a collapsed selection as "the user cleared it".
    const { captured, anchorEl, clear } = useTextSelectionCapture(props.rootRef, props.domConfig, {
        resetKey: props.resetKey,
        frozen: editing !== undefined,
        resolveSection: props.resolveSection,
    });

    const openEditor = () => {
        if (!captured || !anchorEl) {
            return;
        }
        const rect = anchorEl.getBoundingClientRect();
        // A collapsed rect means the selection scrolled out of view or its page was virtualised away.
        // Opening on it would put the popover at the top left corner of the viewport and leave `editing`
        // set, which is the one state that stops the button coming back.
        if (rect.width === 0 && rect.height === 0) {
            return;
        }
        setEditing({ captured, position: { top: rect.bottom, left: rect.left } });
    };

    const closeEditor = () => {
        setEditing(undefined);
        window.getSelection()?.removeAllRanges();
        clear();
    };

    return (
        <>
            {captured && anchorEl && !editing && <SelectionActionButton anchorEl={anchorEl} label={props.config.actionLabel} onClick={openEditor} />}
            {editing && (
                <ClauseSelectionPopover
                    position={editing.position}
                    captured={editing.captured}
                    onCancel={closeEditor}
                    onConfirm={(result) => {
                        closeEditor();
                        props.config.onConfirm(result);
                    }}
                />
            )}
        </>
    );
}
